import { useState } from "react";
import { DataTable } from "@/components/data-table";
import { Form } from "@/components/form";
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    IconButton,
    Stack,
    StackProps,
    Typography
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { searchTurucContribuyentes, TurucContribuyente } from "../api/search-turuc";
import { z } from "zod/v4";
import { useSchemaForm } from "@/components/form";

const searchSchema = z.object({
    search: z.string().min(3, "Mínimo 3 caracteres")
});

interface TurucContribuyenteSearchProps extends StackProps {
    onContribuyenteSelect?: (contribuyente: TurucContribuyente) => void;
    creatingRuc?: string | null;
}

export default function TurucContribuyenteSearch({
    onContribuyenteSelect,
    creatingRuc = null,
    ...props
}: TurucContribuyenteSearchProps) {
    const [results, setResults] = useState<TurucContribuyente[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [searched, setSearched] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");

    const form = useSchemaForm({
        schema: searchSchema,
        onSubmit: async ({ value }) => {
            await handleSearch(value.search, 0);
        },
    });

    const handleSearch = async (search: string, page: number) => {
        setLoading(true);
        setError(null);
        setSearchTerm(search);
        setCurrentPage(page);

        try {
            const response = await searchTurucContribuyentes(search, page);
            setResults(response.data.contribuyentes);
            setTotalPages(response.data.paginas);
            setSearched(true);

            if (response.data.contribuyentes.length === 0) {
                setError("No se encontraron contribuyentes");
            }
        } catch (err: any) {
            setError(err.message || "Error al buscar en DNIT");
            setResults([]);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (newPage: number) => {
        if (searchTerm) {
            handleSearch(searchTerm, newPage);
        }
    };

    const getEstadoColor = (estado: string) => {
        switch (estado.toUpperCase()) {
            case "ACTIVO":
                return "success";
            case "INACTIVO":
            case "CANCELADO":
                return "error";
            case "SUSPENDIDO":
                return "warning";
            default:
                return "default";
        }
    };

    return (
        <Stack minHeight="60vh" gap={2} {...props}>
            <Form form={form} direction="row" flexWrap="wrap" gap={2} alignItems="flex-start" pt={2}>
                <Form.TextField
                    name="search"
                    label="Buscar por RUC o Razón Social"
                    size="small"
                    sx={{ minWidth: 300, flex: 1 }}
                    placeholder="Ej: 80012345-5 o EMPRESA"
                />
                <Form.SubmitButton
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                    disabled={loading}
                >
                    {loading ? "Buscando..." : "Buscar en DNIT"}
                </Form.SubmitButton>
            </Form>

            {error && (
                <Alert severity="info">{error}</Alert>
            )}

            {searched && (
                <DataTable flex={1}>
                    <DataTable.Content
                        fit="flex"
                        rows={results}
                        loading={loading}
                        columns={[
                            {
                                key: "ruc",
                                header: "RUC",
                                width: "120px",
                                render: row => (
                                    <Typography variant="body2" fontWeight="medium">
                                        {row.ruc}
                                    </Typography>
                                )
                            },
                            {
                                key: "razonSocial",
                                header: "Razón Social",
                                render: row => row.razonSocial
                            },
                            {
                                key: "estado",
                                header: "Estado",
                                width: "100px",
                                render: row => (
                                    <Chip
                                        label={row.estado}
                                        size="small"
                                        color={getEstadoColor(row.estado) as any}
                                    />
                                )
                            },
                            {
                                key: "tipo",
                                header: "Tipo",
                                width: "140px",
                                render: row => (
                                    <Chip
                                        label={row.esPersonaJuridica ? "Empresa" : "Persona Física"}
                                        size="small"
                                        variant="outlined"
                                        color={row.esPersonaJuridica ? "primary" : "default"}
                                    />
                                )
                            },
                            {
                                header: "",
                                align: "center",
                                width: "60px",
                                render: row => {
                                    const isThisRowCreating = creatingRuc === row.ruc;
                                    const isAnyCreating = creatingRuc !== null;
                                    return (
                                        <IconButton
                                            onClick={() => onContribuyenteSelect?.(row)}
                                            color="primary"
                                            title="Seleccionar contribuyente"
                                            disabled={isAnyCreating}
                                        >
                                            {isThisRowCreating ? <CircularProgress size={20} /> : <AddIcon />}
                                        </IconButton>
                                    );
                                }
                            }
                        ]}
                    />

                    {totalPages > 1 && (
                        <Stack direction="row" justifyContent="center" alignItems="center" gap={2} py={2}>
                            <Form.Button
                                variant="outlined"
                                size="small"
                                disabled={currentPage === 0 || loading}
                                onClick={() => handlePageChange(currentPage - 1)}
                            >
                                Anterior
                            </Form.Button>
                            <Typography variant="body2">
                                Página {currentPage + 1} de {totalPages}
                            </Typography>
                            <Form.Button
                                variant="outlined"
                                size="small"
                                disabled={currentPage >= totalPages - 1 || loading}
                                onClick={() => handlePageChange(currentPage + 1)}
                            >
                                Siguiente
                            </Form.Button>
                        </Stack>
                    )}
                </DataTable>
            )}

            {!searched && !loading && (
                <Box
                    flex={1}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{ opacity: 0.5 }}
                >
                    <Stack alignItems="center" gap={1}>
                        <SearchIcon sx={{ fontSize: 48 }} />
                        <Typography variant="body2" color="text.secondary">
                            Ingresa un término de búsqueda para comenzar
                        </Typography>
                    </Stack>
                </Box>
            )}
        </Stack>
    );
}
